import React from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import {useEffect} from 'react';

// Puoi usare un'immagine SVG direttamente come componente React per ottimizzare.
// Salva l'SVG seguente in un file (es. /src/assets/404-illustration.svg)
// e importalo. Oppure, per semplicità, lo includo direttamente come stringa data URL.
const SvgIllustration = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 335.2"
        width="100%"
        height="80%"
    >
        <path fill="#e3f2fd" d="M512 335.2H0V0h512z"/>
        <path fill="#bbdefb" d="M103.9 335.2L0 225.8V335.2zM483.5 335.2h-12.8L349.8 190l-39.2 39.2-40.4-40.4 48.7-48.7-23.4-23.4-48.7 48.7-18.9-18.9 23.3-23.3-32.9-32.9-23.3 23.3-18.9-18.9 32.8-32.8-40.9-40.9-32.8 32.8-23.3-23.3-32.9 32.9-23.3-23.3-40.4 40.4L0 225.8V0h512z"/>
        <path fill="#90caf9" d="M0 225.8l120.9 109.4h-17L0 240.1zM512 0v179.3l-59-59-23.4 23.4-48.7-48.7-18.9 18.9 23.3 23.3-32.9 32.9-23.3-23.3-18.9 18.9 32.8 32.8-40.9 40.9-32.8-32.8-23.3 23.3-32.9-32.9-23.3 23.3-40.4-40.4L0 225.8l120.9 109.4L310.6 120.1l-40.4 40.4 48.7 48.7 133.1 126z"/>
        <path fill="#64b5f6" d="M410.6 156.9l-23.4-23.4-59 59V0h12.8v156.9l46.2-46.2zM0 225.8l40.4 40.4 23.3-23.3 32.9 32.9 23.3-23.3 32.8-32.8-18.9-18.9-23.3 23.3-32.9-32.9-23.3 23.3-40.4-40.4zM310.6 120.1l-18.9 18.9 32.8 32.8-40.9 40.9-32.8-32.8-23.3 23.3-32.9-32.9-23.3 23.3-40.4-40.4-40.4 40.4 120.9 109.4h12.8L198.8 214l-40.4 40.4 48.7 48.7 133.1 126h12.8L349.8 190l-39.2 39.2z"/>
        <g fill="#42a5f5">
            <path d="M443.4 205.6l-23.3-23.3-18.9 18.9-48.7-48.7-40.4 40.4 39.2 39.2L198.8 329.3h48.2l120.9-109.4z"/>
            <path d="M281.3 205.6l-23.3-23.3-40.4 40.4 39.2 39.2-23.3 23.3h48.2l23.3-23.3-23.7-23.3zM103.9 335.2l17-15.1-40.4-40.4-40.4 40.4z"/>
        </g>
        <path fill="#2196f3" d="M232.6 306h48.7l23.3-23.3-23.3-23.3-48.7 48.6z"/>
        <text
            x="50%"
            y="50%"
            dominantBaseline="middle"
            textAnchor="middle"
            fill="#1976d2"
            fontSize="180"
            fontFamily="Arial, sans-serif"
            fontWeight="bold"
        >
            404
        </text>
    </svg>
);


const NotFoundPage = () => {
    const navigate = useNavigate();

    useEffect(() => {
        // Quando il componente viene montato, aggiungi la classe al body
        document.body.style.overflow = 'hidden';

        // Quando il componente viene smontato, rimuovi la classe
        // Questa è la "funzione di pulizia"
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    const handleGoHome = () => {
        navigate('/apps');
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                width: '100vw',
                backgroundColor: '#f4f7f9',
                borderRadius: 1,
                overflow: 'hidden', 
            }}
        >
            <Container maxWidth="md" sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    '@keyframes float': {
                        '0%': { transform: 'translateY(0px)' },
                        '50%': { transform: 'translateY(-15px)' },
                        '100%': { transform: 'translateY(0px)' },
                    },
                    '@keyframes fadeIn': {
                        'from': { opacity: 0, transform: 'translateY(10px)' },
                        'to': { opacity: 1, transform: 'translateY(0)' },
                    },
                }}>
                    <Box 
                        sx={{ 
                            maxWidth: { xs: 280, sm: 360, md: 480 }, 
                            width: '100%',
                            mb: { xs: 2, sm: 3 },
                            animation: 'float 6s ease-in-out infinite',
                        }}
                    >
                        <SvgIllustration />
                    </Box>
                    
                    <Box sx={{ animation: 'fadeIn 1s ease-out forwards' }}>
                        <Typography 
                            variant="h3" 
                            component="h1"
                            sx={{
                                fontWeight: 'bold',
                                color: 'text.primary',
                                letterSpacing: '-1px',
                                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                            }}
                        >
                            Oops, pagina non trovata!
                        </Typography>


                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleGoHome}
                            startIcon={<HomeIcon />}
                            sx={{
                                py: 1.5,
                                px: 3,
                                mt: 3,
                                boxShadow: '0 4px 14px 0 rgba(0, 118, 255, 0.39)',
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': {
                                    transform: 'translateY(-3px)',
                                    boxShadow: '0 6px 20px 0 rgba(0, 118, 255, 0.23)'
                                }
                            }}
                        >
                            Torna alla Dashboard
                        </Button>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default NotFoundPage;